import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { fetchJson, assetUrl } from "../lib/api";
import { formatDateFR } from "../lib/format";
import { CARD_SECTION_LABELS, METHOD_LABELS } from "../lib/constants";
import Countdown from "../components/Countdown";
import FighterImage from "../components/FighterImage";
import StatusBadge from "../components/StatusBadge";
import FeaturedVideo from "../components/FeaturedVideo";
import { useSiteSettings } from "../context/SiteSettingsContext";

const useHomeData = (rankingTeaserWcId) => {
  const [data, setData] = useState({
    nextEvent: null,
    mainEvent: null,
    recentResults: [],
    rankings: [],
    fighters: [],
    sponsors: [],
    weightClasses: [],
    news: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    (async () => {
      try {
        // Phase 8 fix — hero event is resolved ENTIRELY by the API
        // (`/home/hero-event`) which applies LIVE → PINNED → next upcoming.
        // We no longer combine two lists client-side (that missed pinned
        // COMPLETED events).
        //
        // Featured fighters: we ask the API for featured-only first. If the
        // admin hasn't featured anyone, we fall back to a plain list so the
        // section is never empty.
        const [
          heroEvent,
          recent, weightClasses,
          fightersFeatured, fightersAll,
          sponsors, liveStatus, news,
        ] = await Promise.all([
          fetchJson("/home/hero-event").catch(() => null),
          fetchJson("/results/recent", { limit: 6 }),
          fetchJson("/weight-classes"),
          fetchJson("/fighters", { featured: true, limit: 12 }).catch(() => []),
          fetchJson("/fighters", { limit: 10 }),
          fetchJson("/sponsors"),
          fetchJson("/live/current").catch(() => null),
          fetchJson("/news", { limit: 3 }).catch(() => []),
        ]);

        const nextEvent = heroEvent && heroEvent.id ? heroEvent : null;
        let mainEvent = null;
        if (nextEvent && nextEvent.slug) {
          const full = await fetchJson(`/events/${nextEvent.slug}`);
          mainEvent = full.fights?.find((f) => f.card_section === "main_event");
        }

        // Phase 8 — ranking teaser division. Admin choice from site_settings
        // wins; otherwise fall back to BUFL flyweight (original default).
        let teaserWc = null;
        if (rankingTeaserWcId) {
          teaserWc = (weightClasses || []).find((w) => w.id === rankingTeaserWcId) || null;
        }
        if (!teaserWc) {
          teaserWc = (weightClasses || []).find(
            (w) => w.display_order === 1 && w.name?.includes("Mouche"),
          );
        }
        let rankings = [];
        if (teaserWc) {
          rankings = await fetchJson("/rankings", {
            weight_class_id: teaserWc.id,
            limit: 5,
          });
        }

        // Featured-first, fallback to full list when no admin selection.
        const featuredNonEmpty = (fightersFeatured || []).length > 0;
        const homeFighters = featuredNonEmpty
          ? (fightersFeatured || []).slice(0, 6)
          : (fightersAll || []).slice(0, 6);

        setData({
          nextEvent,
          mainEvent,
          recentResults: recent || [],
          rankings,
          rankingsWC: teaserWc || null,
          fighters: homeFighters,
          fightersSectionMode: featuredNonEmpty ? "featured" : "all",
          sponsors: sponsors || [],
          weightClasses: weightClasses || [],
          liveStatus: liveStatus || null,
          news: news || [],
          loading: false,
          error: null,
        });
      } catch (e) {
        setData((d) => ({ ...d, loading: false, error: String(e) }));
      }
    })();
  }, [rankingTeaserWcId]);

  return data;
};

const HeroSection = ({ event, mainEvent, liveStatus }) => {
  const { settings } = useSiteSettings();
  const isLive = liveStatus?.state === "live";
  const isCompleted = event?.status === "completed";
  if (!event) {
    const noTitle1 = (settings?.hero_no_event_title || "AUCUN ÉVÉNEMENT").trim() || "AUCUN ÉVÉNEMENT";
    const noTitle2 = (settings?.hero_no_event_title_2 || "ANNONCÉ").trim() || "ANNONCÉ";
    const noSubtitle = (settings?.hero_no_event_subtitle || "La prochaine fight night sera bientôt révélée.").trim() || "La prochaine fight night sera bientôt révélée.";
    return (
      <section className="relative min-h-[80vh] flex items-end border-b border-neutral-900">
        <div className="max-w-[1400px] mx-auto w-full px-4 md:px-8 py-24">
          <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 font-stats">
            Prochaine fight night
          </p>
          <h1 className="mt-6 font-display font-black text-6xl md:text-9xl leading-[0.85] text-white" data-testid="hero-no-event-title">
            {noTitle1}<br />
            <span className="text-neutral-700">{noTitle2}</span>
          </h1>
          <p className="mt-6 max-w-xl text-neutral-400" data-testid="hero-no-event-subtitle">
            {noSubtitle}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      className="relative isolate overflow-hidden border-b border-neutral-900 w-full max-w-full"
      data-testid="home-hero"
    >
      {event.poster_url && (
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <img
            src={assetUrl(event.poster_url)}
            alt=""
            aria-hidden
            fetchpriority="high"
            decoding="async"
            className="w-full h-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-transparent" />
        </div>
      )}

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 pt-16 md:pt-28 pb-16 md:pb-24 min-h-[85vh] flex flex-col justify-end relative">
        {isLive ? (
          <p className="text-xs uppercase tracking-[0.4em] text-burkina-red font-stats inline-flex items-center gap-2" data-testid="hero-live-badge">
            <span className="w-2 h-2 rounded-full bg-burkina-red animate-pulse" /> Fight night · en direct
          </p>
        ) : isCompleted ? (
          <p className="text-xs uppercase tracking-[0.4em] text-gold font-stats" data-testid="hero-completed-eyebrow">
            {event.edition_number
              ? `Résultats officiels · Édition ${String(event.edition_number).padStart(2, "0")}`
              : "Résultats officiels"}
          </p>
        ) : (
          <p className="text-xs uppercase tracking-[0.4em] text-gold font-stats" data-testid="hero-eyebrow">
            {(settings?.hero_eyebrow && settings.hero_eyebrow.trim())
              ? settings.hero_eyebrow
              : (event.edition_number ? `Édition ${String(event.edition_number).padStart(2, "0")}` : "Prochaine fight night")}
          </p>
        )}

        <h1
          className="mt-4 font-display font-black text-white leading-[0.85] break-words max-w-full"
          style={{ fontSize: "clamp(2.5rem, 11vw, 10rem)" }}
        >
          {event.name.replace(/^BUFL\s*—\s*/i, "")}
        </h1>

        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-neutral-300 text-sm md:text-base font-stats uppercase tracking-widest">
          <span data-testid="hero-date">{formatDateFR(event.date, { withGmtTime: true })}</span>
          <span className="w-px h-4 bg-neutral-700" />
          <span className="inline-flex items-center gap-2">
            <MapPin size={14} className="text-gold" />
            {event.venue} · {event.city}
          </span>
        </div>

        <div className="mt-10">
          {isLive ? (
            <Link
              to="/live"
              data-testid="hero-watch-live"
              className="inline-flex items-center gap-3 px-6 h-14 bg-burkina-red text-white font-display uppercase tracking-widest text-sm md:text-base hover:bg-white hover:text-burkina-red transition-colors border border-burkina-red"
            >
              <span className="w-2.5 h-2.5 rounded-full bg-white animate-pulse" />
              Regarder en direct
              <ArrowRight size={18} />
            </Link>
          ) : isCompleted ? (
            <div className="flex flex-wrap items-center gap-4">
              <span
                data-testid="hero-completed-badge"
                className="inline-flex items-center gap-2 h-8 px-3 border border-neutral-700 text-neutral-300 font-stats text-[10px] uppercase tracking-[0.3em]"
              >
                <span className="w-1.5 h-1.5 bg-neutral-400" />
                Terminé
              </span>
              <Link
                to={`/events/${event.slug}`}
                data-testid="hero-see-results"
                className="inline-flex items-center gap-3 px-6 h-14 bg-white text-black font-display uppercase tracking-widest text-sm md:text-base hover:bg-gold hover:text-black transition-colors"
              >
                Voir les résultats
                <ArrowRight size={18} />
              </Link>
            </div>
          ) : (
            <Countdown target={event.date} testId="hero-countdown" />
          )}
        </div>

        {mainEvent && (
          <div className="mt-10 pt-6 border-t border-neutral-800/60 flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-stats">
                {[
                  "Main Event",
                  mainEvent.number_rounds ? `${mainEvent.number_rounds} rounds` : null,
                  mainEvent.weight_class?.name?.split(" (")[0],
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
              <p className="mt-2 font-display font-black text-white text-3xl md:text-5xl">
                {mainEvent.fighter_a?.last_name || mainEvent.fighter_a?.first_name}{" "}
                <span className="text-gold">VS</span>{" "}
                {mainEvent.fighter_b?.last_name || mainEvent.fighter_b?.first_name}
              </p>
            </div>
            <Link
              to={`/events/${event.slug}`}
              data-testid="hero-fightcard-cta"
              className="group inline-flex items-center gap-3 px-6 py-3 border border-white text-white font-display uppercase tracking-widest text-sm hover:bg-white hover:text-black transition-colors duration-200"
            >
              {isCompleted ? "Voir les résultats" : "Voir la fight card"}
              <ArrowRight
                size={16}
                className="transition-transform duration-200 group-hover:translate-x-1"
              />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

const VsSection = ({ fight, event }) => {
  if (!fight) return null;
  const a = fight.fighter_a || {};
  const b = fight.fighter_b || {};
  return (
    <section
      data-testid="home-vs-section"
      className="relative border-b border-neutral-900 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
        <p className="text-xs uppercase tracking-[0.3em] text-gold font-stats">
          Fight Night · Main Event
        </p>
        <h2 className="mt-4 font-display font-black text-4xl md:text-6xl text-white">
          LE FACE-À-FACE
        </h2>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-11 gap-0 md:gap-4 items-stretch">
          {/* Fighter A */}
          <Link
            to={`/fighters/${a.slug}`}
            data-testid="vs-fighter-a"
            className="md:col-span-5 group relative border border-neutral-900 bg-neutral-950 min-h-[440px] flex flex-col justify-end overflow-hidden"
          >
            <FighterImage
              src={assetUrl(a.photo_url)}
              alt={a.first_name}
              className="absolute inset-0"
              imgClassName="object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
            <div className="relative p-6 md:p-8">
              <p className="text-xs uppercase tracking-widest text-neutral-500 font-stats">
                {a.nationality_flag} · {a.city}
              </p>
              <h3 className="mt-2 font-display font-black text-5xl md:text-7xl text-white leading-none">
                {a.last_name || a.first_name}
              </h3>
              {a.nickname && (
                <p className="mt-1 text-gold italic text-sm">"{a.nickname}"</p>
              )}
              <p className="mt-3 font-stats text-sm text-neutral-400 uppercase tracking-widest">
                {a.display_record?.record_str || a.record?.record_str} · #{1}
              </p>
            </div>
          </Link>

          {/* VS divider */}
          <div className="md:col-span-1 flex md:flex-col items-center justify-center py-6 md:py-0">
            <div className="hidden md:block w-px h-24 bg-neutral-800" />
            <span className="font-display font-black text-6xl md:text-8xl text-gold px-4">
              VS
            </span>
            <div className="hidden md:block w-px h-24 bg-neutral-800" />
          </div>

          {/* Fighter B */}
          <Link
            to={`/fighters/${b.slug}`}
            data-testid="vs-fighter-b"
            className="md:col-span-5 group relative border border-neutral-900 bg-neutral-950 min-h-[440px] flex flex-col justify-end overflow-hidden"
          >
            <FighterImage
              src={assetUrl(b.photo_url)}
              alt={b.first_name}
              className="absolute inset-0"
              imgClassName="object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
            <div className="relative p-6 md:p-8 md:text-right">
              <p className="text-xs uppercase tracking-widest text-neutral-500 font-stats">
                {b.nationality_flag} · {b.city}
              </p>
              <h3 className="mt-2 font-display font-black text-5xl md:text-7xl text-white leading-none">
                {b.last_name || b.first_name}
              </h3>
              {b.nickname && (
                <p className="mt-1 text-gold italic text-sm">"{b.nickname}"</p>
              )}
              <p className="mt-3 font-stats text-sm text-neutral-400 uppercase tracking-widest">
                {b.display_record?.record_str || b.record?.record_str} · #{2}
              </p>
            </div>
          </Link>
        </div>

        <div className="mt-10">
          <Link
            to={`/fights/${fight.slug}`}
            data-testid="vs-fight-cta"
            className="inline-flex items-center gap-3 px-6 py-3 bg-white text-black font-display uppercase tracking-widest text-sm hover:bg-gold transition-colors duration-200"
          >
            Voir le combat →
          </Link>
        </div>
      </div>
    </section>
  );
};

const ResultsSection = ({ results }) => (
  <section className="border-b border-neutral-900" data-testid="home-results">
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold font-stats">
            Broadcast
          </p>
          <h2 className="mt-4 font-display font-black text-4xl md:text-6xl text-white">
            DERNIERS RÉSULTATS
          </h2>
        </div>
        <Link
          to="/events"
          className="hidden md:inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white font-display uppercase tracking-widest transition-colors duration-200"
        >
          Voir tout →
        </Link>
      </div>

      {results.length === 0 ? (
        <p className="mt-10 text-neutral-500 uppercase tracking-widest text-sm font-stats border-t border-neutral-900 pt-8">
          Aucun résultat pour l'instant
        </p>
      ) : (
        <div className="mt-10 border-t border-neutral-900">
          {results.map((r) => {
            const winner =
              r.result?.winner_id === r.fighter_a?.id
                ? r.fighter_a
                : r.result?.winner_id === r.fighter_b?.id
                ? r.fighter_b
                : null;
            const loser =
              winner?.id === r.fighter_a?.id ? r.fighter_b : r.fighter_a;
            return (
              <Link
                key={r.id}
                to={`/fights/${r.slug}`}
                data-testid={`result-row-${r.slug}`}
                className="grid grid-cols-12 gap-3 items-center py-5 border-b border-neutral-900 group transition-colors duration-200 hover:bg-neutral-950/60"
              >
                <div className="col-span-5 md:col-span-4">
                  <p className="text-[10px] uppercase tracking-widest text-neutral-600 font-stats">
                    {r.event?.name}
                  </p>
                  <p className="mt-1 font-display font-bold text-white text-lg md:text-2xl leading-tight">
                    <span className="text-white">{winner?.first_name}</span>
                    <span className="text-neutral-600"> def. </span>
                    <span className="text-neutral-500">{loser?.first_name}</span>
                  </p>
                </div>
                <div className="col-span-3 md:col-span-2 font-stats text-xs md:text-sm text-gold uppercase tracking-widest">
                  WIN
                </div>
                <div className="col-span-4 md:col-span-3 font-stats text-xs md:text-sm text-neutral-400 uppercase tracking-widest">
                  {METHOD_LABELS[r.result?.method] || r.result?.method}
                </div>
                <div className="hidden md:block md:col-span-2 font-stats text-sm text-neutral-500 uppercase tracking-widest">
                  R{r.result?.round} · {r.result?.time}
                </div>
                <div className="hidden md:block md:col-span-1 text-right text-neutral-500 group-hover:text-white transition-colors duration-200">
                  →
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  </section>
);

const RankingsSection = ({ rankings, weightClass }) => (
  <section className="border-b border-neutral-900" data-testid="home-rankings">
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4">
        <p className="text-xs uppercase tracking-[0.3em] text-gold font-stats">
          Classement officiel
        </p>
        <h2 className="mt-4 font-display font-black text-4xl md:text-6xl text-white leading-none">
          TOP 5
        </h2>
        {weightClass && (
          <p className="mt-2 font-stats text-sm text-neutral-500 uppercase tracking-widest">
            {weightClass.name}
          </p>
        )}
        <Link
          to="/rankings"
          className="inline-flex items-center gap-2 mt-8 text-sm text-white font-display uppercase tracking-widest border-b border-neutral-700 pb-1 hover:border-gold transition-colors duration-200"
        >
          Voir tous les rankings →
        </Link>
      </div>

      <div className="lg:col-span-8">
        {rankings.length === 0 ? (
          <p className="text-neutral-500 uppercase tracking-widest text-sm font-stats">
            Aucun classement disponible
          </p>
        ) : (
          <div className="border-t border-neutral-800">
            {rankings.slice(0, 5).map((row, i) => {
              const f = row.fighter;
              return (
                <Link
                  key={f.id}
                  to={`/fighters/${f.slug}`}
                  data-testid={`rank-row-${i + 1}`}
                  className="grid grid-cols-12 items-center py-4 border-b border-neutral-900 group hover:bg-neutral-950/60 transition-colors duration-200"
                >
                  <div className="col-span-2 md:col-span-1 font-stats text-3xl md:text-4xl text-gold font-bold">
                    {row.is_champion ? "C" : (row.position ?? i + 1)}
                  </div>
                  <div className="col-span-6 md:col-span-6 font-display font-bold text-white text-xl md:text-2xl uppercase">
                    {f.first_name}{" "}
                    {f.nickname && (
                      <span className="text-neutral-500 italic text-sm normal-case">
                        &quot;{f.nickname}&quot;
                      </span>
                    )}
                  </div>
                  <div className="col-span-4 md:col-span-3 font-stats text-sm text-neutral-400 uppercase tracking-widest">
                    {f.display_record?.record_str || f.record?.record_str}
                  </div>
                  <div className="hidden md:block md:col-span-2 text-right font-stats text-[10px] text-neutral-600 uppercase tracking-widest">
                    {row.has_official ? (row.movement_type === "new" ? "NEW" : row.movement_type === "up" ? `↑ ${row.movement}` : row.movement_type === "down" ? `↓ ${Math.abs(row.movement)}` : "—") : "Provisoire"}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  </section>
);

const FightersDiscovery = ({ fighters }) => (
  <section className="border-b border-neutral-900" data-testid="home-fighters">
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold font-stats">
            Roster BUFL
          </p>
          <h2 className="mt-4 font-display font-black text-4xl md:text-6xl text-white">
            COMBATTANTS À DÉCOUVRIR
          </h2>
        </div>
        <Link
          to="/fighters"
          className="hidden md:inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white font-display uppercase tracking-widest transition-colors duration-200"
        >
          Base complète →
        </Link>
      </div>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        {fighters.map((f, idx) => (
          <Link
            key={f.id}
            to={`/fighters/${f.slug}`}
            data-testid={`fighter-tile-${f.slug}`}
            className={`group relative overflow-hidden border border-neutral-900 bg-neutral-950 aspect-[3/4] flex flex-col justify-end ${
              idx % 2 === 1 ? "md:mt-6" : ""
            }`}
          >
            <FighterImage
              src={assetUrl(f.photo_url)}
              alt={f.first_name}
              className="absolute inset-0"
              imgClassName="object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent" />
            <div className="relative p-3 md:p-4">
              <StatusBadge status={f.status} className="mb-2" />
              <p className="font-display font-black text-2xl md:text-3xl text-white leading-none">
                {f.first_name}
              </p>
              <p className="mt-1 font-stats text-[10px] md:text-xs text-neutral-400 uppercase tracking-widest">
                {f.display_record?.record_str || f.record?.record_str} · {f.current_weight_kg} KG
              </p>
            </div>
          </Link>
        ))}
      </div>

      <Link
        to="/fighters"
        className="md:hidden inline-flex items-center gap-2 mt-6 text-sm text-white font-display uppercase tracking-widest border-b border-neutral-700 pb-1"
      >
        Voir la base complète →
      </Link>
    </div>
  </section>
);

const NewsTeaser = ({ articles = [] }) => (
  <section className="border-b border-neutral-900" data-testid="home-news">
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold font-stats">
            Salle de presse
          </p>
          <h2 className="mt-4 font-display font-black text-4xl md:text-6xl text-white">
            ACTUALITÉS
          </h2>
        </div>
        <Link
          to="/actualites"
          data-testid="home-news-all"
          className="hidden md:inline-flex items-center gap-2 text-xs font-display uppercase tracking-widest text-neutral-400 hover:text-white transition-colors"
        >
          Toutes les actualités →
        </Link>
      </div>

      {articles.length === 0 ? (
        <div className="mt-10 border-t border-neutral-900 pt-10 line-grid min-h-[220px] flex flex-col justify-center items-start px-6 md:px-10 relative">
          <p className="font-display font-black text-2xl md:text-4xl text-neutral-500 leading-tight max-w-2xl">
            AUCUNE ACTUALITÉ POUR L&apos;INSTANT.
            <br />
            <span className="text-neutral-700">
              LES PREMIÈRES ANNONCES ARRIVENT BIENTÔT.
            </span>
          </p>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {articles.slice(0, 3).map((a, i) => (
            <Link
              key={a.id}
              to={`/actualites/${a.slug}`}
              data-testid={`home-news-${a.slug}`}
              className="group flex flex-col border border-neutral-900 hover:border-neutral-700 transition-colors"
            >
              <div className="aspect-[16/10] bg-neutral-950 relative overflow-hidden">
                {a.image_url ? (
                  <img
                    src={assetUrl(a.image_url)}
                    alt={a.title}
                    loading="lazy"
                    className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <p className="font-display font-black text-neutral-800 text-4xl uppercase">BUFL</p>
                  </div>
                )}
                <span className="absolute top-3 left-3 text-[10px] font-stats uppercase tracking-widest bg-black/80 text-gold px-2 py-1">
                  {a.category}
                </span>
              </div>
              <div className="p-4 md:p-5 flex-1 flex flex-col">
                <h3 className="font-display font-black text-white text-xl md:text-2xl uppercase leading-tight group-hover:text-gold transition-colors">
                  {a.title}
                </h3>
                {a.excerpt && (
                  <p className="mt-2 text-neutral-500 text-xs md:text-sm line-clamp-2 flex-1">
                    {a.excerpt}
                  </p>
                )}
                <p className="mt-3 text-[10px] font-stats uppercase tracking-widest text-neutral-600">
                  {formatDateFR(a.published_at || a.created_at)}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}

      <Link
        to="/actualites"
        className="md:hidden inline-flex items-center gap-2 mt-8 text-sm text-white font-display uppercase tracking-widest border-b border-neutral-700 pb-1"
        data-testid="home-news-all-mobile"
      >
        Toutes les actualités →
      </Link>
    </div>
  </section>
);

const SponsorsStrip = ({ sponsors }) => {
  if (!sponsors || sponsors.length === 0) return null;
  return (
    <section className="border-b border-neutral-900" data-testid="home-sponsors">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 md:py-16">
        <p className="text-xs uppercase tracking-[0.3em] text-neutral-600 font-stats text-center">
          Partenaires officiels
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
          {sponsors.map((s) => (
            <div
              key={s.id}
              className="text-neutral-500 font-display font-bold uppercase tracking-widest text-lg md:text-xl border border-neutral-900 px-6 py-3 hover:text-white hover:border-neutral-700 transition-colors duration-200"
            >
              {s.name}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  const { settings } = useSiteSettings();
  const data = useHomeData(settings?.ranking_teaser_weight_class_id || null);

  if (data.loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-neutral-600 font-stats uppercase tracking-widest text-xs animate-pulse">
          Chargement…
        </p>
      </div>
    );
  }

  return (
    <div data-testid="home-page">
      <HeroSection event={data.nextEvent} mainEvent={data.mainEvent} liveStatus={data.liveStatus} />
      <VsSection fight={data.mainEvent} event={data.nextEvent} />
      <ResultsSection results={data.recentResults} />
      {settings?.featured_video_enabled && settings?.featured_video_url && (
        <FeaturedVideo
          url={settings.featured_video_url}
          title={settings.featured_video_title}
        />
      )}
      <RankingsSection rankings={data.rankings} weightClass={data.rankingsWC} />
      <FightersDiscovery fighters={data.fighters} />
      <NewsTeaser articles={data.news} />
      <SponsorsStrip sponsors={data.sponsors} />
    </div>
  );
}
